/**
 * EngineActivityLedgerView (P172) -- a read over the ENGINE_OUTPUT_CHAIN.
 * The tamper-evident, grounded record of every sealed Engine output
 * (P169-171): which surface produced output, the grounding verdict, the
 * model, and the chain row hash. Admin-only; hardcoded English to match
 * the sibling admin consoles.
 */
import { formatDateTime } from "shared/time/formatDate";
import { i18n } from "shared/i18n";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { AdminOnlyPanel } from "shared/admin/AdminOnlyPanel";
import { useMe } from "shared/api/useMe";
import type { EngineOutputRow } from "shared/api/types";
import { useEngineOutputs } from "shared/admin/useEngineOutputs";

/** Round 17 A32 -- the label for a sealed surface. A surface the
 * catalogue does not know reads as "Other Engine output" with its key
 * beside it, never the bare key alone. */
const KNOWN_SURFACES = new Set([
  "assisted_import_mapping",
  "cap_draft_actions",
  "cap_suggest_controls",
  "control_compliance_assessor",
  "control_test_of_design",
  "test_of_design",
  "ibs_admission_score",
  "letter_extract",
  "mock_exam",
  "obligation_summarise",
  "obligation_tag",
  "obligation_find_equivalents",
  "regulatory_change_assess",
]);

export function surfaceLabel(t: TFunction, surface: string): string {
  if (KNOWN_SURFACES.has(surface)) {
    return t(`screens.engineActivityLedgerView.surfaces.${surface}`);
  }
  return `${t("screens.engineActivityLedgerView.surfaces.unknown")} (${surface})`;
}

function GroundingChip({ r }: { r: EngineOutputRow }) {
  const { t } = useTranslation();
  if (r.total_returned === 0) {
    return <span className="text-slate-300">--</span>;
  }
  const clean = r.dropped_ungrounded === 0;
  return (
    <span
      className={`rounded px-2 py-0.5 text-[11px] font-medium ${
        clean ? "bg-rag-greenBg text-rag-green" : "bg-rag-amberBg text-rag-amber"
      }`}
    >
      {r.grounded}/{r.total_returned} {t("screens.engineActivityLedgerView.grounded")}
    </span>
  );
}

export function EngineActivityLedgerView(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const outputs = useEngineOutputs(50);

  if (me.data && !me.data.is_admin) {
    return <AdminOnlyPanel surface="The Engine activity ledger" />;
  }

  return (
    <section data-testid="engine-activity-view" className="flex flex-col gap-4">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
          {t("screens.engineActivityLedgerView.adminEngineActivityLedger")}
        </p>
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon
            name="engine"
            size={20}
            className="me-2 inline-block align-[-0.1em] text-brand-brassDark"
          />
          {t("screens.engineActivityLedgerView.engineActivityLedger")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t(
            "screens.engineActivityLedgerView.everySealedMarsadEngineOutputNewestFirst",
          )}
        </p>
      </header>

      {outputs.isLoading && (
        <p data-testid="engine-activity-loading" className="text-sm text-slate-500">
          {t("screens.engineActivityLedgerView.loadingTheLedger")}
        </p>
      )}
      {outputs.isError && (
        <p data-testid="engine-activity-error" className="text-sm text-rag-red">
          {t("screens.engineActivityLedgerView.couldNotLoadTheEngineActivityLedger")}
        </p>
      )}
      {outputs.data && outputs.data.length === 0 && (
        <p data-testid="engine-activity-empty" className="text-sm text-slate-500">
          {t("screens.engineActivityLedgerView.noSealedEngineOutputsYet")}
        </p>
      )}

      {outputs.data && outputs.data.length > 0 && (
        <article className="rounded-lg border border-slate-200 bg-white p-4">
          <table className="w-full text-start text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-400">
              <tr>
                <th className="py-1">
                  {t("screens.engineActivityLedgerView.surface")}
                </th>
                <th className="py-1">{t("screens.engineActivityLedgerView.model")}</th>
                <th className="py-1">
                  {t("screens.engineActivityLedgerView.grounding")}
                </th>
                <th className="py-1">{t("screens.engineActivityLedgerView.seal")}</th>
                <th className="py-1">{t("screens.engineActivityLedgerView.when")}</th>
              </tr>
            </thead>
            <tbody>
              {outputs.data.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`engine-activity-row-${r.id}`}
                  className="border-t border-slate-100"
                >
                  <td className="py-1.5 text-slate-800">
                    {surfaceLabel(t, r.surface)}
                  </td>
                  <td className="py-1.5 text-slate-500">
                    {r.model ?? "--"}
                    {/* ROUND 15 A15 -- both names, and the difference
                        said, on the ledger as on the examiner's row. */}
                    {r.configured_model != null && r.configured_model !== r.model && (
                      <span
                        data-testid={`engine-activity-configured-${r.id}`}
                        className="ms-1 text-[10px] text-amber-700"
                      >
                        {" "}
                        {t("screens.engineActivityLedgerView.configuredDiffers", {
                          configured: r.configured_model,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5">
                    <GroundingChip r={r} />
                  </td>
                  <td className="py-1.5 font-mono text-[11px] text-slate-400">
                    {r.row_hash.slice(0, 12)}
                  </td>
                  <td className="py-1.5 text-[11px] text-slate-400">
                    {formatDateTime(r.captured_at, i18n.language)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </article>
      )}
    </section>
  );
}
